import { IconChevronRight, IconFolderTile, PlusOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import type { WorkspaceSummary } from '../../workspaces/types'

export interface AssignToProjectModalProps {
  readonly open: boolean
  /** Files in the just-created session — drives the "N files" blurb label. */
  readonly fileCount: number
  readonly sessionName: string
  /** The full joined-or-not list — this component does the `wsJoined`-style
   * filter itself (see the doc comment below), so callers can pass
   * `useWorkspaceList`'s raw result straight through, same as
   * `WorkspaceSelector`. */
  readonly workspaces: readonly WorkspaceSummary[]
  /** True while a PATCH triggered by a previous pick is in flight — disables
   * every action so a second click can't race the first. */
  readonly busy: boolean
  /** Text-carried failure message from a PATCH that didn't succeed (never
   * color-only, WCAG 3.3.1). `null` renders nothing. Deliberately does NOT
   * close the modal — see `HomePage.tsx`'s doc comment on why a failure
   * here must leave the user a retry path rather than silently drop intent. */
  readonly error: string | null
  readonly onSelectWorkspace: (workspaceId: string) => void
  readonly onKeepUnfiled: () => void
  readonly onCreateProject: () => void
  readonly onClose: () => void
}

/**
 * "Add these files to a project" (UID-425, Bug 1) — the home-hero
 * dropzone's post-upload project-assignment prompt, `Fictiv Platform.dc.html
 * :4797-4823`'s `wsAssign` modal. Distinct from `WorkspaceSelector`'s
 * ALREADY-SHIPPED inline "Uploading to" picker on `/uploads` (`prompt:
 * false` in the comp, `:6043-6044`) — this one only fires for the home
 * dropzone/file-input entry points (`prompt: true`, `:6045-6046`), and is a
 * centered modal shown AFTER the session already exists (`:6140` sets
 * `assignModal` alongside `activeSessionId`), not a picker shown before
 * upload.
 *
 * Copy is verbatim from the comp: heading "Add these files to a project"
 * (`:4808`), blurb "{{fileLabel}} from "{{sessionName}}". Pick a project so
 * your team can find these parts — or keep them unfiled." (`:4809`), and the
 * "Keep unfiled" button (`:4817`).
 *
 * The comp's own `options` derivation (`:8791`,
 * `_projList.filter(p=>this.wsJoined(p.id))`) lists ONLY projects the caller
 * has actually joined — never a locked/non-member row, unlike
 * `WorkspaceSelector`'s defensive (and today-unreachable) locked-row branch
 * for the inline `/uploads` picker. This component matches that: it filters
 * `workspaces` to `myRole !== null` itself, so callers can hand it the same
 * raw `useWorkspaceList` result `WorkspaceSelector` gets.
 *
 * NO PRESELECTION: the comp gives every row the identical "member"
 * affordance regardless of pick state — there is no `selected`/`sel` field
 * anywhere in `wsAssign.options` (contrast `uploadProj.options`, which DOES
 * carry `sel`, for the inline picker's default-selected first-joined
 * project). A modal that fires mid-upload, unprompted, is exactly the place
 * an accidental Enter/click on a silently-preselected row would misfile a
 * batch — so this deliberately preselects nothing; the caller (`HomePage`)
 * never seeds an initial choice for it.
 *
 * Reuses `Modal` (focus trap, Escape, backdrop-dismiss, focus-return) rather
 * than hand-rolling a dialog — the same reasoning ADR-0124 gives for
 * `ConfirmDialog`. This component does NOT own the create-project modal —
 * same split as `WorkspaceSelector`: it only invokes `onCreateProject`, and
 * the page (`HomePage`) decides what that means.
 *
 * Each project row is a plain, individually Tab-focusable `<button>` rather
 * than a `role="menu"`/`menuitem` roving-arrow-key widget (contrast
 * `WorkspaceSelector`, which IS a menu popped from a trigger and so needs
 * roving focus per ARIA APG). This is a body of a modal that already traps
 * focus and gives every row a native Tab stop — imposing menu semantics on
 * top would obligate arrow-key handling for a widget that isn't a menu and
 * gain nothing a screen-reader user doesn't already get from Tab/Shift+Tab.
 */
export function AssignToProjectModal({
  open,
  fileCount,
  sessionName,
  workspaces,
  busy,
  error,
  onSelectWorkspace,
  onKeepUnfiled,
  onCreateProject,
  onClose,
}: AssignToProjectModalProps) {
  const joinedWorkspaces = workspaces.filter((workspace) => workspace.myRole !== null)
  const fileLabel = `${fileCount} ${fileCount === 1 ? 'file' : 'files'}`

  return (
    // UID-508 — the comp's `wsAssign` card (`Fictiv Platform.dc.html:4799`) is
    // ONE continuous 24px-padded block: icon+title, blurb, list, footer, with
    // no divider lines and no × close button. `hideHeader` suppresses the
    // shared shell's bordered header bar (and its close button, which the
    // design doesn't have — dismissal is Escape/backdrop/"Keep unfiled"), and
    // `className` flattens the body/footer padding+borders and applies the
    // design's own deeper `0 12px 32px rgba(0,0,0,.2)` shadow — the same
    // "scope a one-off borderless card via className" precedent as
    // `.rfq-bulk-confirm-modal`, not a change to any shared token. The icon
    // badge + title are rendered as the body's own first block. `title` is
    // still required and drives the dialog's accessible name via aria-label.
    <Modal
      open={open}
      onClose={onClose}
      title="Add these files to a project"
      hideHeader
      maxWidth={470}
      className="assign-to-project-modal"
      footer={
        <>
          <Button
            variant="outline"
            className="assign-to-project-new-project"
            onClick={onCreateProject}
            disabled={busy}
          >
            <PlusOutlined aria-hidden="true" />
            New project
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="outline" onClick={onKeepUnfiled} disabled={busy}>
            Keep unfiled
          </Button>
        </>
      }
    >
      <div className="assign-to-project-fields">
        <div className="assign-to-project-header">
          <span className="assign-to-project-title-icon" aria-hidden="true">
            <IconFolderTile size={20} />
          </span>
          <h2 className="assign-to-project-title">Add these files to a project</h2>
        </div>
        {error && (
          <p role="alert" className="assign-to-project-error">
            {error}
          </p>
        )}
        <p className="assign-to-project-blurb">
          {fileLabel} from “{sessionName}”. Pick a project so your team can find these parts — or
          keep them unfiled.
        </p>
        <div className="assign-to-project-list">
          {joinedWorkspaces.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              className="assign-to-project-row"
              disabled={busy}
              onClick={() => onSelectWorkspace(workspace.id)}
            >
              <span className="assign-to-project-tile" aria-hidden="true">
                <IconFolderTile size={17} />
              </span>
              <span className="assign-to-project-row-text">
                <span className="assign-to-project-row-name">{workspace.name}</span>
                <span className="assign-to-project-row-sub">
                  {workspace.memberCount} {workspace.memberCount === 1 ? 'member' : 'members'}
                </span>
              </span>
              <span className="assign-to-project-row-chevron" aria-hidden="true">
                <IconChevronRight size={16} strokeWidth={2.5} />
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  )
}
